import { useState, useEffect } from 'react';
import { Card, Button, Badge, getAuthToken } from '@trafiflow/shared-ui';
import { Plus, Edit2, Trash2, User, Mail, Phone, Shield, Loader2, AlertCircle } from 'lucide-react';
import { API_CONFIG } from '../../../shared/config';

interface Person {
    id: string; // Internal ID
    name: string;
    email: string;
    roleId?: string; // Organization Role (defined in Org -> Roles)
    securityRole: 'Super Admin' | 'Admin' | 'Member'; // Platform Security Role
    mobile?: string;
    authUserId?: string; // Association with login user
}

interface Role {
    id: string;
    name: string;
}

const SECURITY_ROLES = ['Super Admin', 'Admin', 'Member'] as const;

interface OrphanedUser {
    email: string;
    userId: string;
    username: string;
}

export default function People() {
    const [people, setPeople] = useState<Person[]>([]);
    const [orgRoles, setOrgRoles] = useState<Role[]>([]);
    const [orphanedUsers, setOrphanedUsers] = useState<OrphanedUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingOrphaned, setIsLoadingOrphaned] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isFixing, setIsFixing] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPerson, setEditingPerson] = useState<Person | null>(null);

    // Form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState('');
    const [securityRole, setSecurityRole] = useState<Person['securityRole']>('Member');
    const [mobile, setMobile] = useState('');

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const token = await getAuthToken();
            const [peopleRes, rolesRes] = await Promise.all([
                fetch(`${API_CONFIG.baseUrl}/org/people`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_CONFIG.baseUrl}/org/roles`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const peopleData = await peopleRes.json();
            const rolesData = await rolesRes.json();

            if (peopleData.success) setPeople(peopleData.data || []);
            if (rolesData.success) setOrgRoles(rolesData.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrphanedUsers = async () => {
        setIsLoadingOrphaned(true);
        try {
            const token = await getAuthToken();
            const response = await fetch(`${API_CONFIG.baseUrl}/org/people/orphaned`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            if (data.success) {
                setOrphanedUsers(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching orphaned users:', error);
        } finally {
            setIsLoadingOrphaned(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchOrphanedUsers();
    }, []);

    const handleOpenModal = (person?: Person) => {
        if (person) {
            setEditingPerson(person);
            setName(person.name);
            setEmail(person.email);
            setRoleId(person.roleId || '');
            setSecurityRole(person.securityRole);
            setMobile(person.mobile || '');
        } else {
            setEditingPerson(null);
            setName('');
            setEmail('');
            setRoleId('');
            setSecurityRole('Member');
            setMobile('');
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!name || !email) {
            alert('Name and Email are required.');
            return;
        }

        setIsSaving(true);
        try {
            const token = await getAuthToken();
            const response = await fetch(`${API_CONFIG.baseUrl}/org/people`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    id: editingPerson?.id,
                    name,
                    email,
                    roleId: roleId || undefined,
                    securityRole,
                    mobile: mobile || undefined,
                    authUserId: editingPerson?.authUserId
                })
            });

            const result = await response.json();
            if (result.success) {
                await fetchData();
                setIsModalOpen(false);
            }
        } catch (error) {
            console.error('Error saving person:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this person? This will also affect their login association.')) return;

        try {
            const token = await getAuthToken();
            const response = await fetch(`${API_CONFIG.baseUrl}/org/people/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setPeople(people.filter((p: Person) => p.id !== id));
            }
        } catch (error) {
            console.error('Error deleting person:', error);
        }
    };

    const handleFixOrphanedUser = async (email: string) => {
        setIsFixing(email);
        try {
            const token = await getAuthToken();
            const response = await fetch(`${API_CONFIG.baseUrl}/org/people/orphaned/fix`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ email })
            });

            const result = await response.json();
            if (result.success) {
                // Refresh both lists
                await fetchData();
                await fetchOrphanedUsers();
            } else {
                alert(`Failed to fix user: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error fixing orphaned user:', error);
            alert('Failed to fix user. Please try again.');
        } finally {
            setIsFixing(null);
        }
    };

    const getOrgRoleName = (id?: string) => {
        if (!id) return null;
        return orgRoles.find((r: Role) => r.id === id)?.name || null;
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-tf-text">People</h1>
                    <p className="text-sm text-tf-text-muted">Manage team members and their application access</p>
                </div>
                <Button onClick={() => handleOpenModal()} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Person
                </Button>
            </div>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-tf-bg-muted border-b border-tf-border">
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Member</th>
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Contact</th>
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Org Role</th>
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Security Role</th>
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-tf-border">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-10 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                            <span className="text-sm text-tf-text-muted">Loading team members...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : people.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-tf-text-muted">
                                        No team members found.
                                    </td>
                                </tr>
                            ) : (
                                people.map((person: Person) => (
                                    <tr key={person.id} className="hover:bg-tf-bg-muted transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary-lighter flex items-center justify-center text-primary font-bold">
                                                    {person.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-tf-text">{person.name}</div>
                                                    <div className="text-xs text-tf-text-muted font-mono">
                                                        {person.authUserId ? `ID: ${person.authUserId}` : 'Not registered yet'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-sm text-tf-text">
                                                    <Mail className="w-3 h-3 text-tf-text-muted" />
                                                    {person.email}
                                                </div>
                                                {person.mobile && (
                                                    <div className="flex items-center gap-2 text-sm text-tf-text-muted">
                                                        <Phone className="w-3 h-3" />
                                                        {person.mobile}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {person.roleId ? (
                                                <Badge variant="info">
                                                    <div className="flex items-center gap-1">
                                                        <Shield className="w-3 h-3" />
                                                        {getOrgRoleName(person.roleId)}
                                                    </div>
                                                </Badge>
                                            ) : (
                                                <span className="text-tf-text-muted italic text-sm">No role</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={person.securityRole === 'Super Admin' ? 'critical' : person.securityRole === 'Admin' ? 'warning' : 'default'}>
                                                {person.securityRole}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenModal(person)}
                                                    className="p-2 text-tf-text-muted hover:text-primary hover:bg-primary-lighter rounded transition-all"
                                                    disabled={isSaving}
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(person.id)}
                                                    className="p-2 text-tf-text-muted hover:text-critical hover:bg-critical-lighter rounded transition-all"
                                                    disabled={isSaving}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Users Requiring Attention */}
            <Card className="overflow-hidden">
                <div className={`px-6 py-4 border-b border-tf-border ${orphanedUsers.length > 0 ? 'bg-tf-feedback-warning-bg' : 'bg-tf-bg-muted'}`}>
                    <div className="flex items-center gap-2">
                        <AlertCircle className={`w-5 h-5 ${orphanedUsers.length > 0 ? 'text-tf-feedback-warning-text' : 'text-tf-text-muted'}`} />
                        <h2 className="text-lg font-semibold text-tf-text">Users Requiring Attention</h2>
                    </div>
                    <p className="text-sm text-tf-text-muted mt-1">
                        These users exist in Cognito but are not in the database. Click "Fix" to sync them.
                    </p>
                </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-tf-bg-muted border-b border-tf-border">
                                    <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-tf-border">
                                {isLoadingOrphaned ? (
                                    <tr>
                                        <td colSpan={2} className="px-6 py-10 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                                <span className="text-sm text-tf-text-muted">Loading users...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : orphanedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={2} className="px-6 py-10 text-center text-sm text-tf-text-muted">
                                            No users requiring attention.
                                        </td>
                                    </tr>
                                ) : (
                                    orphanedUsers.map((user: OrphanedUser) => (
                                        <tr key={user.email} className="hover:bg-tf-bg-muted transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-sm text-tf-text">
                                                    <Mail className="w-4 h-4 text-tf-text-muted" />
                                                    {user.email}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Button
                                                    onClick={() => handleFixOrphanedUser(user.email)}
                                                    disabled={isFixing === user.email}
                                                    variant="primary"
                                                    className="flex items-center gap-2"
                                                >
                                                    {isFixing === user.email ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                            Fixing...
                                                        </>
                                                    ) : (
                                                        'Fix'
                                                    )}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>

            {/* Modal People */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <Card className="w-full max-w-md p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded bg-primary-lighter flex items-center justify-center text-primary">
                                <User className="w-6 h-6" />
                            </div>
                            <h2 className="text-xl font-bold">{editingPerson ? 'Edit Person' : 'Add Person'}</h2>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-tf-text mb-1">Full Name *</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-3 py-2 border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                        placeholder="e.g. Douglas Rodriguez"
                                        disabled={isSaving}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-tf-text mb-1">Mobile (Optional)</label>
                                    <input
                                        type="tel"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value)}
                                        className="w-full px-3 py-2 border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                        placeholder="+1..."
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-tf-text mb-1">Email Address *</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-3 py-2 border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                    placeholder="e.g. douglas@trafilea.com"
                                    disabled={isSaving}
                                />
                                <p className="text-[10px] text-tf-text-muted mt-1 italic">Note: This email will be used for login association.</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-tf-text mb-1">Organization Role</label>
                                    <select
                                        value={roleId}
                                        onChange={(e) => setRoleId(e.target.value)}
                                        className="w-full px-3 py-2 border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                        disabled={isSaving}
                                    >
                                        <option value="">None</option>
                                        {orgRoles.map(r => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-tf-text mb-1">Security Role *</label>
                                    <select
                                        value={securityRole}
                                        onChange={(e) => setSecurityRole(e.target.value as Person['securityRole'])}
                                        className="w-full px-3 py-2 border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all font-semibold"
                                        disabled={isSaving}
                                    >
                                        {SECURITY_ROLES.map(role => (
                                            <option key={role} value={role}>{role}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={isSaving}>Cancel</Button>
                                <Button onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? (
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </div>
                                    ) : (editingPerson ? 'Update Person' : 'Create Person')}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
